import React from 'react';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  IdentificationIcon,
  ShieldCheckIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { StudentSession } from '../../types';

interface ExamsScheduleTabProps {
  session?: StudentSession | null;
}

const ExamsScheduleTab: React.FC<ExamsScheduleTabProps> = ({ session }) => {
  return (
    <div className="space-y-6">
      {/* ── Main Hero Card: Exam Dates Coming Soon ── */}
      <div className="bg-gradient-to-br from-amber-950/80 via-slate-900 to-orange-950/70 border border-amber-500/40 rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden backdrop-blur-xl">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="space-y-4 max-w-2xl">
            <div className="flex items-center space-x-2.5 flex-wrap gap-y-2">
              <span className="px-3.5 py-1.5 rounded-full text-xs font-black tracking-wider bg-amber-500 text-slate-950 uppercase shadow-lg flex items-center space-x-2">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-950 animate-ping" />
                <span>EXAM DATES COMING SOON</span>
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-amber-200 border border-amber-500/30">
                Phase 2 Examination &amp; Evaluation
              </span>
            </div>

            <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight">
              Kreed Odisha Phase 2 Examinations
            </h2>

            <p className="text-sm sm:text-base text-amber-100/90 leading-relaxed">
              The official examination dates and schedule for enrolled Phase 2 candidates will be announced here soon. All candidates are advised to attend live classes regularly and keep their portal access active.
            </p>

            {session && (
              <div className="pt-2 flex flex-wrap items-center gap-3 text-xs text-amber-200/90 font-medium">
                <span className="flex items-center space-x-1.5 bg-black/40 px-3.5 py-1.5 rounded-xl border border-white/15">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-400" />
                  <span>Candidate: <strong className="text-white">{session.studentName || 'Enrolled Student'}</strong></span>
                </span>
                <span className="flex items-center space-x-1.5 bg-black/40 px-3.5 py-1.5 rounded-xl border border-white/15">
                  <IdentificationIcon className="w-4 h-4 text-amber-400" />
                  <span>Reg ID: <strong className="text-white">{session.regNoSuffix || '000'}</strong></span>
                </span>
                {session.batchLabel && (
                  <span className="flex items-center space-x-1.5 bg-black/40 px-3.5 py-1.5 rounded-xl border border-white/15">
                    <span>Batch: <strong className="text-white">{session.batchLabel}</strong></span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Status Badge Card */}
          <div className="bg-black/50 border border-amber-500/40 rounded-2xl p-6 flex flex-col items-center justify-center text-center sm:min-w-[280px] shadow-2xl space-y-3.5 self-start md:self-auto">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/40 flex items-center justify-center animate-pulse">
              <CalendarDaysIcon className="w-8 h-8" />
            </div>

            <div>
              <span className="text-[11px] font-bold text-amber-300 uppercase tracking-wider block">
                Schedule Status
              </span>
              <span className="text-2xl font-black text-white mt-1 block">
                Dates Coming Soon
              </span>
            </div>

            <p className="text-xs text-slate-300 leading-snug">
              Official announcement will be published directly on this portal.
            </p>

            <div className="w-full pt-2 border-t border-white/10 flex items-center justify-center space-x-2 text-emerald-400 text-xs font-semibold">
              <CheckCircleIcon className="w-4 h-4" />
              <span>Notification Alerts Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Compact Notification Info Bar ── */}
      <div className="p-4 sm:p-5 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md flex items-center space-x-3 text-xs text-blue-100 shadow-md">
        <BellAlertIcon className="w-5 h-5 text-amber-400 shrink-0" />
        <p>
          Exam schedules and reporting instructions will appear here as soon as they are declared. Please check back regularly.
        </p>
      </div>
    </div>
  );
};

export default ExamsScheduleTab;
